'use client';

import type { ErrorDetails } from '@/types/api';
import { useAuth } from '@clerk/clerk-react';
import axios, { type AxiosError, type AxiosRequestConfig, type CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef } from 'react';

// Define error types
export class ApiError extends Error {
  status?: number;
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class NetworkError extends ApiError {
  constructor(message = 'Network error. Please check your connection.') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class AuthenticationError extends ApiError {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message, 401);
    this.name = 'AuthenticationError';
  }
}

// Establish default timeout
const DEFAULT_TIMEOUT = 30000; // 30 seconds

const rawBaseApiPath = import.meta.env.VITE_BASE_API_PATH as string;

/** "v0" or "v1" */
const apiVersion = rawBaseApiPath.endsWith('/v0') ? 'v0' : 'v1';
const defaultRegion = 'ap-southeast-2'; // Default region for API calls

// Valid regions for API calls
const VALID_REGIONS = ['ap-southeast-2', 'us-west-2'];

// Get region from localStorage or use default
const getRegion = (): string => {
  if (typeof window === 'undefined') {
    return defaultRegion;
  }

  const storedRegion = localStorage.getItem('region');

  // Validate the region is one of the allowed values
  if (storedRegion && VALID_REGIONS.includes(storedRegion)) {
    return storedRegion;
  }

  return defaultRegion;
};

// Track consecutive errors to prevent excessive retries
const ERROR_COUNTER = {
  MAX_ERRORS: 5,
  ERROR_WINDOW_MS: 60_000, // 1 minute

  // Reset counter if window has passed
  checkAndIncrement() {
    const now = Date.now();
    const lastErrorTime = parseInt(sessionStorage.getItem('api_error_time') || '0', 10);
    let count = parseInt(sessionStorage.getItem('api_error_count') || '0', 10);

    if (now - lastErrorTime > this.ERROR_WINDOW_MS) {
      count = 0;
    }

    count++;
    sessionStorage.setItem('api_error_count', count.toString());
    sessionStorage.setItem('api_error_time', now.toString());

    console.log('API error count:', count);
    console.log('count > this.MAX_ERRORS:', count > this.MAX_ERRORS);

    return count > this.MAX_ERRORS;
  },

  reset() {
    sessionStorage.removeItem('api_error_count');
    sessionStorage.removeItem('api_error_time');
  },
};

export function useAuthenticatedApi() {
  const { getToken } = useAuth();
  const cancelTokensRef = useRef<CancelTokenSource[]>([]);

  // Create a ref to store the current region
  const regionRef = useRef<string>(getRegion());

  // Update the region when localStorage changes
  useEffect(() => {
    // Function to update the region
    const updateRegion = () => {
      regionRef.current = getRegion();
    };

    // Initial update
    updateRegion();

    // Listen for storage events (in case region is changed in another tab)
    window.addEventListener('storage', (event) => {
      if (event.key === 'region') {
        updateRegion();
      }
    });

    return () => {
      window.removeEventListener('storage', updateRegion);
    };
  }, []);

  // Create API client with dynamic baseURL
  const getApiClient = useCallback(() => {
    const region = regionRef.current;
    const baseApiPath = `/api-${region}/${apiVersion}`;

    return axios.create({
      baseURL: baseApiPath,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: DEFAULT_TIMEOUT,
    });
  }, []);

  // Clean up pending requests on unmount
  useEffect(() => {
    return () => {
      cancelTokensRef.current.forEach((source) => {
        source.cancel('Request canceled due to component unmount');
      });
      cancelTokensRef.current = [];
    };
  }, []);

  // Generate request key for deduplication
  const getRequestKey = (endpoint: string, method: string, data?: unknown): string => {
    return `${method}:${endpoint}:${data ? JSON.stringify(data) : ''}`;
  };

  // Track pending requests to prevent duplicates
  const pendingRequests = useRef(new Map<string, Promise<unknown>>()).current;

  const apiCall = useCallback(
    async <T = unknown, D = unknown>(
      endpoint: string,
      method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
      data?: D,
      options: {
        timeout?: number;
      } = {}
    ): Promise<T> => {
      const { timeout = DEFAULT_TIMEOUT } = options;

      // Create a unique identifier for this request
      const requestKey = getRequestKey(endpoint, method, data);

      // Check if this exact request is already in progress
      if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey) as Promise<T>;
      }

      // Create function for making the actual request
      const makeRequest = async (): Promise<T> => {
        // Get fresh token
        const token = await getToken({ template: 'default' });

        if (!token) {
          throw new AuthenticationError();
        }

        // Create cancel token
        const cancelTokenSource = axios.CancelToken.source();
        cancelTokensRef.current.push(cancelTokenSource);

        // Get a fresh API client with the current region
        const apiClient = getApiClient();

        const config: AxiosRequestConfig = {
          method,
          url: endpoint,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data,
          timeout,
          cancelToken: cancelTokenSource.token,
        };

        // Handle query params for GET requests
        if (data && (method === 'GET' || method === 'DELETE')) {
          const queryParams: Record<string, string> = {};

          // Convert complex objects to strings for query params
          Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              queryParams[key] = JSON.stringify(value);
            } else if (value !== undefined && value !== null) {
              queryParams[key] = String(value);
            }
          });

          const queryString = new URLSearchParams(queryParams).toString();
          if (queryString) {
            config.url += `?${queryString}`;
          }
        }

        try {
          const response = await apiClient(config);

          // Remove from cancelTokens
          cancelTokensRef.current = cancelTokensRef.current.filter((source) => source !== cancelTokenSource);

          // Reset the error counter on success
          ERROR_COUNTER.reset();

          return response.data as T;
        } catch (error) {
          // Remove from cancelTokens
          cancelTokensRef.current = cancelTokensRef.current.filter((source) => source !== cancelTokenSource);

          if (axios.isCancel(error)) {
            console.warn('Request canceled:', error.message);
            // just return "undefined" if the request was canceled:
            return undefined as T;
          }

          if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;

            // Handle request timeouts and network errors
            if (!axiosError.response || axiosError.code === 'ECONNABORTED') {
              throw new NetworkError();
            }

            ERROR_COUNTER.checkAndIncrement();

            // Handle specific HTTP status codes
            switch (axiosError.response?.status) {
              case 401:
                throw new AuthenticationError();
              case 403:
                throw new ApiError("You don't have permission to access this resource", 403);
              case 404:
                return null as T; // Return null for 404 errors
              case 429:
                throw new ApiError('Too many requests. Please try again later', 429);
              case 500:
              case 502:
              case 503:
              case 504:
                throw new ApiError('Server error. Please try again later', axiosError.response.status);
              default:
                throw new ApiError(
                  (axiosError.response?.data as ErrorDetails).errorDetails || 'An error occurred',
                  axiosError.response?.status
                );
            }
          }

          // For non-axios errors
          console.error('API call error:', error);
          throw new ApiError('An unexpected error occurred');
        }
      };

      // Create the request promise
      const requestPromise = makeRequest();

      // Store in pending requests
      pendingRequests.set(requestKey, requestPromise);

      try {
        // Wait for the result
        const result = await requestPromise;
        return result;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(requestKey);
      }
    },
    [getToken, getApiClient]
  );

  return {
    apiCall,
    currentRegion: regionRef.current,
    validRegions: VALID_REGIONS,
  };
}
